import { Tab, Tabs, Typography } from '@material-ui/core';
import { WarningRounded } from '@material-ui/icons';
import {
  FormikTextField,
  SuspendedFormikPhoneField,
  useFormikEnhanced,
} from '@superdispatch/forms';
import {
  Color,
  Column,
  Columns,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { useEffect, useState } from 'react';
import { APIError } from 'shared/api/APIError';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useInitialValues } from 'shared/form/AppFormik';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { useVerifiedCarrierApplicationStatus } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusAPI';
import { useInsurances } from 'shared/modules/carrier-profile/core/CargoInsuranceList';
import { CargoInsurancePreview } from 'shared/modules/carrier-profile/core/CargoInsurancePreview';
import { ProfileCargoInsuranceForm } from 'shared/modules/carrier-profile/core/ProfileCargoInsuranceForm';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import { useCarrierProfileAPI } from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import {
  CargoInsuranceDTO,
  cargoInsuranceSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import { useCargoInsuranceExpiring } from './core/CargoInsuranceExpiring';
import {
  InsuranceExpiringBanner,
  RenewalInsurancePendingBanner,
  RenewalInsuranceVerifiedBanner,
  ReviewedBanner,
} from './core/InsuranceBanners';
import {
  useSubmitVerificationDocument,
  VerificationDialogs,
} from './core/VerificationDialogs';
import { VerifyCertificateHolderDialog } from './core/VerifyCertificateHolderDialog';
import { ProfilePageLayout } from './ProfilePageLayout';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { ExpediteApprovalBanner } from 'shared/modules/carrier-profile/core/InsuranceBanners';

export function ProfileCargoInsurancePage() {
  const { addSnackbar } = useSnackbarStack();
  const [selectedInsurance, setSelectedInsurance] = useState<
    'current' | 'renewal'
  >('current');
  const [currentModal, setCurrentModal] = useState<
    'verify-certificate-holder' | undefined
  >();

  const { updateInsurance, createInsurance } = useCarrierProfileAPI();
  const { data } = useVerifiedCarrierApplicationStatus();
  const verifiedCarrierApplication = data?.verified_carrier_application;
  const isApplicationReviewed =
    verifiedCarrierApplication?.status === 'reviewed';

  const { data: settings } = useCarrierSettings();
  const isEditingDisabled =
    !settings?.drivers_can_edit_carrier_info && MobileAppBridge.isInjected();

  const { currentInsurance, renewalInsurance, isInitialLoading, isError } =
    useInsurances();

  const {
    isInsuranceExpiring,
    isRenewalInsuranceUploaded,
    isRenewalInsuranceVerified,
  } = useCargoInsuranceExpiring();
  const shouldShowRenewalTab =
    isInsuranceExpiring && verifiedCarrierApplication?.status === 'approved';

  useEffect(() => {
    setSelectedInsurance(shouldShowRenewalTab ? 'renewal' : 'current');
  }, [shouldShowRenewalTab, setSelectedInsurance]);

  const initialValues = useInitialValues(
    cargoInsuranceSchema,
    (selectedInsurance === 'current' ? currentInsurance : renewalInsurance) ||
      {},
  );

  const formik = useFormikEnhanced({
    initialValues,
    validationSchema: cargoInsuranceSchema,
    onSubmit: (values) => {
      return values.guid ? updateInsurance(values) : createInsurance(values);
    },
    onSubmitSuccess: (_: APIResponse<CargoInsuranceDTO>, values) => {
      addSnackbar('Cargo insurance was successfully updated', {
        variant: 'success',
      });

      trackCarrierProfileEvent({
        name: 'Carrier Updated Carrier Info',
        utm_medium: 'Carrier Profile',
        utm_content:
          selectedInsurance === 'current'
            ? 'Cargo Insurance'
            : 'Cargo Insurance Renewal',
        is_new_document: !values.guid,
      });
    },
    onSubmitFailure: ({ message }: APIError) => {
      addSnackbar(`Failed to update cargo insurance: ${message}`, {
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    if (isError) {
      addSnackbar('Failed to fetch cargo insurance', { variant: 'error' });
    }
  }, [isError, addSnackbar]);

  const submitVerificationForm = useSubmitVerificationDocument(formik);

  return (
    <>
      <DocumentTitle title="Cargo Insurance - Profile" />

      <VerificationDialogs formik={formik} />

      <VerifyCertificateHolderDialog
        open={currentModal === 'verify-certificate-holder'}
        onClose={() => {
          setCurrentModal(undefined);
        }}
        onSubmit={() => {
          setCurrentModal(undefined);
          formik.handleSubmit();
        }}
      />

      <ProfilePageLayout
        formik={formik}
        size={isWebView ? 'small' : isApplicationReviewed ? 'large' : 'medium'}
        title="Cargo Insurance"
        isLoading={isInitialLoading}
        actions={
          <Button
            type="button"
            onClick={() => {
              if (selectedInsurance === 'current') {
                submitVerificationForm();
              } else {
                setCurrentModal('verify-certificate-holder');
              }
            }}
            pending={formik.isSubmitting}
            disabled={!formik.dirty}
            fullWidth={isWebView}
          >
            Save
          </Button>
        }
        extraDetails={<CargoInsurancePreview />}
      >
        <Stack space="small">
          {shouldShowRenewalTab && (
            <Tabs
              value={selectedInsurance}
              onChange={(_, next: 'current' | 'renewal') => {
                setSelectedInsurance(next);
              }}
            >
              <Tab value="current" label="Current" />
              <Tab
                value="renewal"
                label={
                  <Inline space="xxsmall" verticalAlign="center">
                    Renewal{' '}
                    {!isRenewalInsuranceUploaded && (
                      <WarningRounded
                        htmlColor={Color.Yellow300}
                        fontSize="small"
                      />
                    )}
                  </Inline>
                }
              />
            </Tabs>
          )}

          {selectedInsurance === 'current' && isApplicationReviewed && (
            <ReviewedBanner />
          )}

          {selectedInsurance === 'renewal' && (
            <Stack space="small">
              <RenewalBanners />
              {!isRenewalInsuranceVerified && <ExpediteApprovalBanner />}
            </Stack>
          )}

          <Box maxWidth={['496px', '496px', '440px']}>
            <Stack space="large">
              <ProfileCargoInsuranceForm />

              <Stack space="small">
                <Typography variant="h4">Agent</Typography>

                <FormikTextField
                  label={
                    <>
                      Name <TextBox color="secondary">(Optional)</TextBox>
                    </>
                  }
                  name="agent_name"
                  disabled={isEditingDisabled}
                  fullWidth={true}
                />

                <Columns space="small" collapseBelow="tablet">
                  <Column width={['fluid', '1/2']}>
                    <SuspendedFormikPhoneField
                      label={
                        <>
                          Phone <TextBox color="secondary">(Optional)</TextBox>
                        </>
                      }
                      name="agent_phone"
                      disabled={isEditingDisabled}
                      fullWidth={true}
                    />
                  </Column>

                  <Column width={['fluid', '1/2']}>
                    <FormikTextField
                      label={
                        <>
                          Email <TextBox color="secondary">(Optional)</TextBox>
                        </>
                      }
                      name="agent_email"
                      disabled={isEditingDisabled}
                      fullWidth={true}
                    />
                  </Column>
                </Columns>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </ProfilePageLayout>
    </>
  );
}

function RenewalBanners() {
  const { currentInsurance } = useInsurances();
  const { isRenewalInsuranceUploaded, isRenewalInsuranceVerified } =
    useCargoInsuranceExpiring();

  if (isRenewalInsuranceVerified && !!currentInsurance) {
    return (
      <RenewalInsuranceVerifiedBanner
        expirationDate={currentInsurance.expiration_date}
      />
    );
  }

  if (isRenewalInsuranceUploaded) {
    return <RenewalInsurancePendingBanner />;
  }

  return <InsuranceExpiringBanner />;
}
